/**
 * PricingPanel.tsx — vehicle-based live pricing (GUI v2 feature 5).
 *
 * Every number is a deterministic lookup from live/content/pricing.ts (req
 * 5.2) — before the vehicle resolves, the panel prompts for capture, never
 * placeholder numbers (req 5.1). Premium-first presentation per req 5.4/A.10:
 * the recommended package (Black Ceramic standard start, escalating per the
 * matrix) is anchored; the full tier table expands beneath (req A.7 parity).
 * Assumptions are flagged on the price itself (req 5.6); detected add-ons
 * auto-surface even while collapsed (req A.7).
 */
import { useState } from "react";
import type { CallCardState } from "@/types";
import {
  ADD_ONS,
  CLASS_LABEL,
  DEPOSIT_CENTS,
  PREPAY_THRESHOLD_CENTS,
  TIERS,
  UV_LINE,
  formatCents,
  quoteSummary,
  recommendTier,
  tier,
} from "@/live/content/pricing";
import { CONSULT_PATHS } from "@/live/content/close";
import { PanelLabel } from "@/live/panels";

export function PricingPanel({
  card,
  expanded,
  onCopySummary,
}: {
  card: CallCardState;
  /** Full-screen renders expanded; the sidebar collapses behind EXPAND (9.1/A.7). */
  expanded: boolean;
  onCopySummary?: (text: string) => void;
}) {
  const [open, setOpen] = useState(expanded);
  const vehicle = card.vehicles[card.activeVehicle] ?? card.vehicles[0];
  const interest = card.serviceInterest.value;

  // PPF / coating: no invented packages or prices — consult/inspection is the
  // close (req G.6). Framework slots render as visibly pending.
  if (interest === "ppf" || interest === "ceramic_coating") {
    const path = interest === "ppf" ? CONSULT_PATHS.ppf : CONSULT_PATHS.coating;
    return (
      <section aria-label="Pricing" className="space-y-1.5">
        <PanelLabel>Pricing</PanelLabel>
        <div className="rounded-lg border bg-card p-3">
          <p className="text-sm font-semibold">{path.label}</p>
          <p className="mt-1 text-sm text-muted-foreground">“{path.line}”</p>
          <p className="mt-2 rounded-md border border-dashed border-amber-400 bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            Packages + prices pending owner definition — never quote a number by phone.
          </p>
        </div>
      </section>
    );
  }

  if (!vehicle || vehicle.pricingClass === null) {
    return (
      <section aria-label="Pricing" className="space-y-1.5">
        <PanelLabel>Pricing</PanelLabel>
        <div className="rounded-lg border border-dashed bg-card p-3">
          <p className="text-sm font-semibold">Capture the vehicle to show exact pricing.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {vehicle?.ambiguous
              ? "Vehicle heard but the class is unclear — ask: “Is that the sedan or the SUV?”"
              : "Tint is flat rate by vehicle class — year, make, and model is all it takes."}
          </p>
        </div>
      </section>
    );
  }

  const cls = vehicle.pricingClass;
  const reasons = card.reasons.map((r) => ({ key: r.key, confirmed: r.confirmed }));
  const rec = recommendTier(reasons.map((r) => r.key));
  const recTier = tier(rec.anchor);
  const recPrice = recTier.fullPackageCents[cls];
  const stepUp = rec.stepUp ? tier(rec.stepUp) : null;
  const overThousand = recPrice >= PREPAY_THRESHOLD_CENTS;
  const summary = quoteSummary(vehicle, rec.anchor);
  const showRemoval = vehicle.existingTint === true;
  const newVehicle = card.reasons.some((r) => r.key === "new_vehicle");

  return (
    <section aria-label="Pricing" className="space-y-1.5">
      <div className="flex items-center justify-between">
        <PanelLabel>Pricing</PanelLabel>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
          Showing {CLASS_LABEL[cls]} pricing only{vehicle.classAssumed ? " (assumed)" : ""}
        </span>
      </div>

      {/* Recommended anchor (premium-first per the matrix + A.10 policy) */}
      <div className="rounded-lg border-2 border-primary/40 bg-card p-3">
        <div className="flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-primary">Recommended</p>
            <p className="truncate text-base font-semibold">{recTier.name} · full sides + rear</p>
            <p className="text-xs text-muted-foreground">{recTier.tser} · {recTier.irr}</p>
          </div>
          <p className="text-2xl font-bold tabular-nums">
            {formatCents(recPrice)}
            {vehicle.classAssumed && <span className="align-super text-sm text-amber-500" title="Class assumed — confirm body style">*</span>}
          </p>
        </div>
        {stepUp && (
          <p className="mt-1.5 text-sm text-muted-foreground">
            Step up: <span className="font-medium text-foreground">{stepUp.name} {formatCents(stepUp.fullPackageCents[cls])}</span>
            <span className="ml-1 text-xs">({stepUp.irr}, {stepUp.lifeHappens} Life Happens)</span>
          </p>
        )}
        <p className="mt-1 text-xs text-muted-foreground">{rec.reason}</p>
        {overThousand && (
          <p className="mt-1.5 rounded-md bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            Over $1,000 — disclose 50% at drop-off at booking (deposit counts toward it).
          </p>
        )}
        {summary && (
          <button
            type="button"
            onClick={() => onCopySummary?.(summary)}
            className="mt-2 w-full truncate rounded-md border px-2 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent"
            title="Tap to copy"
          >
            “{summary}” ⧉
          </button>
        )}
      </div>

      {/* Detected add-ons auto-surface even while collapsed (req A.7) */}
      {showRemoval && (
        <div className="flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-sm dark:bg-amber-950/30">
          <span className="font-medium">Existing tint → removal</span>
          <span className="tabular-nums">{formatCents(ADD_ONS.removal.cents)}/hr, quoted up front</span>
        </div>
      )}
      {newVehicle && (
        <div className="rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground">
          New vehicle — offer windshield ceramic ({formatCents(ADD_ONS.windshieldAirCeramic.cents)} / i3 {formatCents(ADD_ONS.windshieldI3.cents)}) and mention PPF.
        </div>
      )}

      {!expanded && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="w-full rounded-md border px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent">
          {open ? "COLLAPSE" : "EXPAND — all tiers + add-ons"}
        </button>
      )}

      {(expanded || open) && (
        <div className="overflow-hidden rounded-lg border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50 text-left text-xs text-muted-foreground">
                <th className="px-2.5 py-1.5 font-medium">Tier</th>
                <th className="px-2 py-1.5 font-medium">Full pkg</th>
                <th className="px-2 py-1.5 font-medium">Front 2</th>
                <th className="px-2 py-1.5 font-medium">Specs</th>
              </tr>
            </thead>
            <tbody>
              {TIERS.map((t) => (
                <tr key={t.key} className={`border-b last:border-0 ${t.key === rec.anchor ? "bg-accent/60" : ""}`}>
                  <td className="px-2.5 py-1.5">
                    <span className="font-medium">{t.name.replace("autobahn ", "")}</span>
                    <span className="ml-1 text-[11px] text-muted-foreground">{t.ladder}</span>
                  </td>
                  <td className="px-2 py-1.5 font-semibold tabular-nums">{formatCents(t.fullPackageCents[cls])}</td>
                  <td className="px-2 py-1.5 tabular-nums">{formatCents(t.frontTwoCents)}</td>
                  <td className="px-2 py-1.5 text-[11px] text-muted-foreground">{t.tser} · {t.irr}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="space-y-0.5 border-t px-2.5 py-1.5 text-xs text-muted-foreground">
            <p>Windshield: AirCeramic/Black Ceramic {formatCents(ADD_ONS.windshieldAirCeramic.cents)} · i3 {formatCents(ADD_ONS.windshieldI3.cents)} · Brow {formatCents(ADD_ONS.brow.cents)}</p>
            <p>Removal {formatCents(ADD_ONS.removal.cents)}/hr · Deposit {formatCents(DEPOSIT_CENTS)} (applies to total) · {UV_LINE}</p>
          </div>
        </div>
      )}
    </section>
  );
}
